import { cn } from "@-/com.npmjs.cn";
import { View, type ViewProps } from "@-/com.npmjs.react-native";
import Animated, { type AnimatedStyle } from "@-/com.npmjs.react-native-reanimated";
import type { ReactNode } from "react";
import type { StyleProp, ViewStyle } from "@-/com.npmjs.react-native";

const cornerStyle = { borderCurve: "continuous" as const };

/**
 * The floating flashcard surface shared by Learn faces (fronts, backs and Info) and profile cards.
 *
 * The outer wrapper carries the face animation; the inner surface clips content to its border.
 * The enclosing card stage casts one shadow around the body and binder tabs together, and only
 * after this card is promoted to current. Individual faces never cast overlapping shadows.
 *
 * A face with `binderTabs` draws them in its own top 30pt, above a body whose top edge they draw.
 * They are children of the same animated view as the body, so they share its every transform,
 * opacity change and mount: a face can never show without its tabs, nor its tabs without it.
 */
export function LearnFlashcardShell({
  animatedStyle,
  binderTabs,
  children,
  className,
  contentClassName,
  pointerEvents,
  style,
  testID,
}: {
  /** Reanimated transform for the whole card (drag, tilt, lift, growth). */
  animatedStyle?: AnimatedStyle<ViewStyle>;
  /** The face's binder tabs (see LearnFlashcardBinderTabs); the body leaves its top edge to them. */
  binderTabs?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  pointerEvents?: ViewProps["pointerEvents"];
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  return (
    <Animated.View
      className={cn("rounded-xs", className)}
      pointerEvents={pointerEvents}
      style={[cornerStyle, style, animatedStyle]}
      testID={testID}
    >
      {binderTabs == null ? (
        <View
          className={cn(
            "flex-1 overflow-hidden rounded-xs border border-border bg-surface",
            contentClassName,
          )}
          style={cornerStyle}
        >
          {children}
        </View>
      ) : (
        <>
          <View className="h-7.5" pointerEvents="box-none">
            {binderTabs}
          </View>
          <View className="flex-1">
            <View
              className={cn(
                "flex-1 overflow-hidden rounded-b-xs bg-surface px-px pb-px",
                contentClassName,
              )}
              style={cornerStyle}
            >
              {children}
            </View>
            <View
              className="absolute inset-0 rounded-b-xs border-x border-b border-border"
              pointerEvents="none"
              style={cornerStyle}
            />
          </View>
        </>
      )}
    </Animated.View>
  );
}
